import { TAB_PROPS, FIELD_PROPS, INNER_FIELD_PROPS, TEMPLATE_PROPS } from '../constants'
import { editTemplate, saveTemplate, saveUserConfig, resetUserConfig } from './http-data'
import { updateConfigByRemote, updateConfigs } from './normalize-table'

/**
 * 获取当前字段配置（转换为原始格式）
 * @param {String} menuId - 菜单ID
 * @param {Array} internalConfigs - 内部配置数据
 */
function getCurrentFieldsConfig(menuId, internalConfigs) {
  return internalConfigs.map(tab => {
    const fields = tab[TAB_PROPS.FIELDS]
    const fieldContent = fields.filter(item => item[INNER_FIELD_PROPS.SHOW]).map(item => item[FIELD_PROPS.KEY])
    const fieldSnapshot = fields.reduce((prev, cur, index) => {
      prev[cur[FIELD_PROPS.KEY]] = { show: cur[INNER_FIELD_PROPS.SHOW], index: index }
      return prev
    }, {})
    return {
      menuId,
      fieldContent: JSON.stringify(fieldContent),
      fieldSnapshot: JSON.stringify(fieldSnapshot),
      [TAB_PROPS.GENERIC_ID]: tab[TAB_PROPS.GENERIC_ID],
    }
  })
}

/**
 * 获取当前模板配置（转换为原始格式）
 * @param {String} menuId - 菜单ID
 * @param {Array} items - 配置数据
 * @param {String} templateModel - 模板详情
 */
function getCurrentTemplatesConfig({ menuId, items, templateModel }) {
  const internalItems = getCurrentFieldsConfig(menuId, items)
  return internalItems.map(item => {
    return {
      ...item,
      templateName: templateModel[TEMPLATE_PROPS.LABEL]
    }
  })
}

/**
 * 统一保存逻辑：自定义回调 > 仅触发事件
 * @param {Object} context - Vue 组件实例
 * @param {Object} params - 参数对象
 * @param {String} params.menuId - 菜单ID
 * @param {Object} params.items - 配置数据
 * @param {Function} params.saveFn - 自定义保存回调
 * @param {String} params.eventName - 事件名称
 * @returns {Promise} 保存结果
 */
export async function executeSave(context, { menuId, items, saveFn, eventName }) {
  try {
    const internalItems = getCurrentFieldsConfig(menuId, items)
    // 优先级1：自定义回调
    if (typeof saveFn === 'function') {
      // await saveFn(internalItems)
      // context.$emit('update:items', items)
      context.$emit('input', false)
      return
    }
    // 优先级2：仅触发事件
    internalItems.map(v => saveUserConfig(v))
    context.$emit('update:items', items)
    // 兼容表格配置更新
    updateConfigs(context, items)
    context.$emit(`${eventName}`, internalItems)
    context.$message.success('保存成功')
    context.$emit('input', false)
  } catch (error) {
    console.error(error)
    // context.$message.error('保存失败')
  } finally {
  }
}

/**
 * @param {Object} context - Vue 组件实例
 * @param {Object} params - 参数对象
 * @param {String} params.menuId - 菜单ID
 * @param {Object} params.item - 配置数据
 * @param {Number} params.index - 配置数据索引
 * @param {String} params.eventName - 事件名称
 * @returns {Promise} 重置结果
 */
export async function executeReset(context, { menuId, item, index, eventName }) {
  try {
    let params = {
      menuId,
      genericId: item[TAB_PROPS.GENERIC_ID]
    }
    if (item[TAB_PROPS.AUTH_URL]) {
      params.apiCode = item[TAB_PROPS.AUTH_URL]
    }
    const res = await resetUserConfig(params)
    // 兼容表格配置更新
    updateConfigByRemote(context, { fieldContent: res.genericSearch, jobTemplate: res.jobTemplate, item, index })
    context.$emit(`${eventName}`)
    context.$message.success('重置成功')
    context.$emit('input', false)
  } catch (error) {
    console.error(error)
    // context.$message.error('重置失败')
  } finally {
  }
}

/**
 * 保存模板逻辑
 * @param {Object} context - Vue 组件实例
 * @param {Object} params - 参数对象
 * @param {String} params.menuId - 菜单ID
 * @param {Object} params.items - 配置数据
 * @param {String} params.templateModel - 模板详情
 * @param {Array} params.templates - 所有模板
 * @returns {Promise} 保存结果
 */
export async function executeSaveTemplate(context, { menuId, items, templateModel, templates, isCreate }) {
  let data = getCurrentTemplatesConfig({ menuId, items, templateModel, templates })
  let template = templates.find(item => item[TEMPLATE_PROPS.BATCH_ID] === templateModel[TEMPLATE_PROPS.BATCH_ID])
  if (!isCreate) {
    data = data.map(item => {
      const findItem = template[TEMPLATE_PROPS.FIELDS].find(t => item[TAB_PROPS.GENERIC_ID] === t[TAB_PROPS.GENERIC_ID])
      return {
        ...item,
        batchId: templateModel[TEMPLATE_PROPS.BATCH_ID],
        id: findItem.id,
      }
    })
  }
  try {
    await saveTemplate(data)
    isCreate && context.getTemplateListByIds()
    context.$message.success('保存模板成功')
  } catch (error) {
    console.error(error)
    // context.$message.error('保存模板失败')
  } finally {
  }
}

/**
 * 保存模板名称逻辑
 * @param {Object} context - Vue 组件实例
 * @param {Object} params - 参数对象
 * @param {String} params.menuId - 菜单ID
 * @param {Object} params.items - 配置数据
 * @param {String} params.templateModel - 模板详情
 * @param {Array} params.templates - 所有模板
 * @returns {Promise} 保存结果
 */
export async function executeSaveTemplateName(context, { menuId, items, templateModel, templates, isEditName }) {
  // 保存模板名称逻辑
  if (isEditName) {
    await editTemplate(templateModel)
    const template = templates.find(item => item[TEMPLATE_PROPS.BATCH_ID] === templateModel[TEMPLATE_PROPS.BATCH_ID])
    if (template) {
      template[TEMPLATE_PROPS.LABEL] = templateModel.templateName
    }
    context.$message.success('保存模板名称成功')
    return
  }

  await executeSaveTemplate(context, { menuId, items, templateModel, templates, isCreate: true })
}



// WEBPACK FOOTER //
// ./src/public/components/kye-field-settings/data/save-operations.js